import { User, LogOut, Settings, Sun, Moon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { RootState } from "@/store";
import { toggleTheme } from "@/store/slices/uiSlice";

import { useAuth } from "../../hooks/useAuth";
import { ROUTES } from "../../utils/constants";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useSelector((state: RootState) => state.ui);

  // Apply theme forcefully to document element for portals that creates by shadcn components when displaying components like popover/dropdown
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    // data attribute for additional targeting
    root.setAttribute("data-theme", theme);
  }, [theme]);

  const handleLogout = () => {
    logout();
    navigate(ROUTES.HOME);
  };

  const getDashboardRoute = useCallback(() => {
    if (!user) return ROUTES.HOME;
    switch (user.role) {
      case "fan":
        return ROUTES.DASHBOARD.FAN;
      case "organizer":
        return ROUTES.DASHBOARD.ORGANIZER;
      case "manager":
        return ROUTES.DASHBOARD.MANAGER;
      case "superadmin":
        return ROUTES.DASHBOARD.ADMIN;
      default:
        return ROUTES.HOME;
    }
  }, [user]);

  const themeToggle = () => {
    dispatch(toggleTheme());
  };

  return (
    <header className='sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
      <div className='container flex h-16 items-center justify-between'>
        <div className='flex items-center space-x-8'>
          <Link to={ROUTES.HOME} className='flex items-center space-x-2'>
            <img src='/images/logos/LK/logo-lk.png' alt='PlayArena' className='h-8 w-8' />
            <span className='text-xl font-bold'>PlayArena</span>
          </Link>

          <nav className='hidden items-center space-x-6 md:flex'>
            <Link
              to={ROUTES.GROUNDS}
              className='text-sm font-medium transition-colors hover:text-primary'
            >
              Grounds
            </Link>
            <Link
              to={ROUTES.EVENTS}
              className='text-sm font-medium transition-colors hover:text-primary'
            >
              Events
            </Link>
          </nav>
        </div>

        <div className='flex items-center space-x-4'>
          <Button variant='ghost' size='icon' onClick={themeToggle} aria-label='Toggle Theme'>
            {theme === "light" ? (
              <Sun className='!size-6 stroke-2 text-yellow-400 transition-colors hover:text-yellow-600' />
            ) : (
              <Moon className='!size-6 stroke-2 text-yellow-400 transition-colors hover:text-yellow-600' />
            )}
          </Button>

          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' className='relative h-8 w-8 rounded-full'>
                  <Avatar className='h-8 w-8'>
                    <AvatarImage
                      src={user.profile.avatar || "/placeholder.svg"}
                      alt={user.profile.name}
                    />
                    <AvatarFallback>{user.profile.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='w-56' align='end' forceMount>
                <div className='flex items-center justify-start gap-2 p-2'>
                  <div className='flex flex-col space-y-1 leading-none'>
                    <p className='font-medium'>{user.profile.name}</p>
                    <p className='w-[200px] truncate text-sm text-muted-foreground'>{user.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to={getDashboardRoute()}>
                    <User className='mr-2 h-4 w-4' />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={ROUTES.PROFILE}>
                    <Settings className='mr-2 h-4 w-4' />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className='mr-2 h-4 w-4' />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className='flex items-center space-x-2'>
              <Button variant='ghost' asChild>
                <Link to={ROUTES.LOGIN}>Sign In</Link>
              </Button>
              <Button asChild>
                <Link to={ROUTES.REGISTER}>Sign Up</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
